import { db } from '../firebase'; // Go up one directory to find firebase.js
import { collection, addDoc, doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

/**
 * Saves a new stack document or updates an existing one in Firestore,
 * including associated health goals and notes.
 * 
 * @param {object} stackCoreData - Core data for the stack (name, supplements, origin, version, etc.). 
 *                                 Should NOT include refs or timestamps - they are handled here.
 * @param {string} healthGoalsContent - The text content for the health goals.
 * @param {string} notesContent - The text content for the notes.
 * @param {string|null} stackId - The ID of the stack document to update. If null or 
 *                                undefined, a new document will be created.
 * @returns {Promise<void>} A promise that resolves when the operation is complete.
 * @throws {Error} Throws an error if the Firestore operation fails or userId is missing.
 */
export const saveOrUpdateStack = async (stackCoreData, healthGoalsContent, notesContent, stackId = null) => {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("User not authenticated.");
  }
  const userId = user.uid;

  let finalHealthGoalsRefId = null;
  let finalNotesRefId = null;

  if (stackId) {
    // --- Update Existing Stack ---
    const stackRef = doc(db, 'stacks', stackId);
    const stackSnap = await getDoc(stackRef);

    if (!stackSnap.exists()) {
      throw new Error(`Stack with ID ${stackId} not found.`);
    }
    const existingStackData = stackSnap.data();
    
    // Update health goals if ref exists
    if (existingStackData.healthGoalsRef) {
      try {
        const healthGoalsRef = doc(db, 'healthGoals', existingStackData.healthGoalsRef);
        await updateDoc(healthGoalsRef, { goals: healthGoalsContent || "" });
        finalHealthGoalsRefId = existingStackData.healthGoalsRef;
      } catch (error) {
         console.warn(`[stackActions Update] Failed to update health goals for ref ${existingStackData.healthGoalsRef}:`, error);
         // Decide if you want to re-throw, proceed without update, or try to create a new one
         // For now, we'll proceed and the stack will keep its old (potentially invalid) ref
         finalHealthGoalsRefId = existingStackData.healthGoalsRef; 
      }
    } else {
       console.warn(`[stackActions Update] healthGoalsRef missing for stack ${stackId}. Cannot update goals.`);
       // Optionally create a new one here if desired
    }

    // Update notes if ref exists
    if (existingStackData.notesRef) {
      try {
        const notesRef = doc(db, 'notes', existingStackData.notesRef);
        await updateDoc(notesRef, { content: notesContent || "" });
        finalNotesRefId = existingStackData.notesRef;
      } catch (error) {
         console.warn(`[stackActions Update] Failed to update notes for ref ${existingStackData.notesRef}:`, error);
         finalNotesRefId = existingStackData.notesRef;
      }
    } else {
      console.warn(`[stackActions Update] notesRef missing for stack ${stackId}. Cannot update notes.`);
      // Optionally create a new one here
    }
    
    // Prepare stack data for update
    const dataToUpdate = {
      ...stackCoreData, // contains name, supplements etc.
      healthGoalsRef: finalHealthGoalsRefId,
      notesRef: finalNotesRefId,
      updatedAt: serverTimestamp(),
      // Ensure fields only relevant at creation are not included
      ...(existingStackData.version && { version: existingStackData.version }), // Preserve original version unless overwritten in stackCoreData
      ...(existingStackData.origin && { origin: existingStackData.origin }),   // Preserve original origin unless overwritten
    };
    delete dataToUpdate.userId; // userId is immutable
    delete dataToUpdate.createdAt; // createdAt is immutable
    
    await updateDoc(stackRef, dataToUpdate);

  } else {
    // --- Create New Stack ---
    // Create new health goals document
    try {
      const healthGoalDocRef = await addDoc(collection(db, 'healthGoals'), {
        goals: healthGoalsContent || "",
        userId: userId
      });
      finalHealthGoalsRefId = healthGoalDocRef.id;
    } catch (error) {
      console.error("[stackActions Create] Failed to create health goals document:", error);
      throw new Error("Failed to save health goals."); // Re-throw as it's critical for a new stack
    }

    // Create new notes document
    try {
      const notesDocRef = await addDoc(collection(db, 'notes'), {
        content: notesContent || "",
        userId: userId
      });
      finalNotesRefId = notesDocRef.id;
    } catch (error) {
      console.error("[stackActions Create] Failed to create notes document:", error);
      // If notes fail, maybe we can proceed without it? Or maybe not. 
      // For now, let's throw, assuming notes are important.
      // Cleanup the healthGoalDoc if needed (or use a transaction)
      // await deleteDoc(doc(db, 'healthGoals', finalHealthGoalsRefId)); // Example cleanup
      throw new Error("Failed to save notes."); 
    }

    // Prepare stack data for creation
    const dataToCreate = {
      ...stackCoreData, // contains name, supplements, origin, version etc.
      userId: userId,
      healthGoalsRef: finalHealthGoalsRefId,
      notesRef: finalNotesRefId,
      createdAt: serverTimestamp(), 
      updatedAt: serverTimestamp(),
    };
    
    // Ensure essential fields for creation are present
    if (!dataToCreate.userId) {
      console.error("[stackActions Create] Attempted to create a stack without a userId!");
      throw new Error("Cannot create stack: userId is missing.");
    }
     if (!dataToCreate.origin) { // Example check
        console.warn("[stackActions Create] Stack origin is missing, defaulting to 'unknown'.");
        dataToCreate.origin = 'unknown';
     }

    await addDoc(collection(db, 'stacks'), dataToCreate);
  }
}; 